import React from 'react';
import Snackbar from 'material-ui/Snackbar';
import Joi from 'joi-browser';
import _ from 'lodash';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { animateScroll as scroll } from 'react-scroll';
import Actions from '../actions';
import ContractForm from '../components/ContractForm';
import ContractNav from '../components/ContractNav';
import formTypes from '../formTypes';
import validationTypes from '../validationTypes';
import schemaObjectify from '../utilities/schemaObjectify';
import { fieldDefinitionsToContractSummaryClasses } from '../utilities/helpers';

function loadDependencies(fieldDefinitionsSerializable) {
  // this function relies on a formTypes being accessible.
  const fieldDefinitions = {};
  const keys = Object.keys(fieldDefinitionsSerializable);
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    const fieldDefinitionSerializable = fieldDefinitionsSerializable[key];
    const component = formTypes[fieldDefinitionSerializable.ComponentName];
    const validationSchema = validationTypes[fieldDefinitionSerializable.validationSchema];
    const fieldDefinition = {
      ...fieldDefinitionSerializable,
      ComponentName: component, // TODO: possibly rename this?
      validationSchema,
    };
    fieldDefinitions[key] = fieldDefinition;
  }
  return fieldDefinitions;
}

function mapDispatchToProps(dispatch) {
  return {
    onChange(id, value) {
      dispatch(Actions.handledChange(id, value));
    },
    onValidatedField(id, message) {
      dispatch(Actions.handledValidatedField(id, message));
    },
    onValidatedFields(errors) {
      dispatch(Actions.handledValidatedFields(errors));
      // TODO: populate an actual error message.
      const errorResponse = (
        <div>You have made a few errors, please correct them before proceeding.</div>
      );
      dispatch(Actions.setMessage(errorResponse));
      // Scroll back up to see errors.
      scroll.scrollTo(250);
    },
    onSubmit(userValues) {
      dispatch(Actions.savedContract(userValues));
      dispatch(Actions.resetForm());
      // dispatch loading action.
      // here send the data to server => blockchain, or just blockchain.
      // get a message back to be displayed to user.
      const serverResponse = <div>Done! Visit the Manage Contracts page to view your contract.</div>;
      // dispatch loading false action.

      dispatch(Actions.setMessage(serverResponse));
    },
    dismissMessage() {
      const emptyString = '';
      dispatch(Actions.setMessage(emptyString));
    },
  };
}

function mapStateToProps(state) {
  return {
    userInput: state.contractForm.userInput,
    validationErrors: state.contractForm.validationErrors,
    fieldDefinitionsSerializable: state.contractForm.fieldDefinitionsSerializable,
    uiState: state.contractForm.uiState,
  };
}

class ContractFormContainer extends React.Component {
  constructor(props) {
    super(props);
    this.handleSubmit = this.handleSubmit.bind(this);
  }
  componentDidMount() {
    // thoughts for the future: here, either do a fetch to initialize the data into state,
    // or prepopulate the state through the store or reducers (if there is a default).
    // currently I have set a default through the reducer.
    // data returned could look something like:
    // [{field_name: address, field_type: text_field, value: ""} ,...,
    // {field_name: ether, field_type: text_AREA, value: ""}].
    // This data could be inputted by the one who created the solidity conctract.
    // the shape of the data can be discussed, but i want to highlight the content of the data.
    // validation rules could also be good to have.
    // the field_names and field_types could be used as as an input into
    // a higher order component HOCContractForm that will return ContractForm.
    // with JOI: here fetch would be made, and defaultFieldDefinitions would be put into state.
    // For now, I'm just faking that I have it in render method.
  }
  validateFields() {
    const { fieldDefinitionsSerializable, userInput } = this.props;
    const fieldDefinitions = loadDependencies(fieldDefinitionsSerializable);
    const validationSchema = schemaObjectify(fieldDefinitions);

    const errors = {};
    /* eslint-disable no-shadow, no-unused-vars */

    if (_.includes(Object.keys(userInput), 'id')) {
      // A => B so no need to do A and B. B = _.includes(Object.keys(userInput), 'balance')
      const thaRealUserInput = _.omit(userInput, 'id', 'balance');
      const { error: joiErrors } = Joi.validate(thaRealUserInput, validationSchema, {
        abortEarly: false,
      });
      if (!_.isEmpty(joiErrors)) {
        // `joiErrors` is a map from field key to an error object.
        const { details } = joiErrors;
        _.each(details, (detailItem) => {
          const { message, path } = detailItem;
          // Determine the input name from the path array.
          const elementName = _.last(path);
          // Add the message to errors.
          errors[elementName] = message;
        });
      }
    } else {
      const { error: joiErrors } = Joi.validate(userInput, validationSchema, {
        abortEarly: false,
      });
      if (!_.isEmpty(joiErrors)) {
        // `joiErrors` is a map from field key to an error object.
        const { details } = joiErrors;
        _.each(details, (detailItem) => {
          const { message, path } = detailItem;
          // Determine the input name from the path array.
          const elementName = _.last(path);
          // Add the message to errors.
          errors[elementName] = message;
        });
      }
    }
    /* eslint-enable no-shadow no-unsed-vars */

    return errors;
  }
  handleSubmit() {
    const errors = this.validateFields();
    const { onSubmit, userInput, onValidatedFields } = this.props;

    if (_.isEmpty(errors)) {
      onSubmit(userInput);
    } else {
      onValidatedFields(errors);
    }
  }

  render() {
    const {
      uiState,
      userInput,
      validationErrors,
      onChange,
      onValidatedField,
      fieldDefinitionsSerializable,
      dismissMessage,
    } = this.props;

    const fieldDefinitions = loadDependencies(fieldDefinitionsSerializable);
    const validationSchema = schemaObjectify(fieldDefinitions);
    const snackbarMessage = uiState ? uiState.message : '';
    //  eslint-disable-next-line max-len
    const contractSummaryClasses = fieldDefinitionsToContractSummaryClasses(fieldDefinitionsSerializable);
    return (
      <div>
        <div className="page-title">
          <h1>Create Contract</h1>
        </div>
        <ContractNav />
        <ContractForm
          fieldDefinitions={fieldDefinitions}
          validationSchema={validationSchema}
          validationErrors={validationErrors}
          userInput={userInput}
          onChange={onChange}
          onValidatedField={onValidatedField}
          onSubmit={this.handleSubmit}
          contractSummaryClasses={contractSummaryClasses}
        />
        <Snackbar
          open={!!snackbarMessage}
          message={snackbarMessage}
          onRequestClose={dismissMessage}
          className="form-message"
        />
      </div>
    );
  }
}

// disabled beacuse shape of object will differ in the future as it is dynamically generated.
/* eslint-disable react/forbid-prop-types, react/require-default-props */
ContractFormContainer.propTypes = {
  uiState: PropTypes.object,
  userInput: PropTypes.object,
  fieldDefinitionsSerializable: PropTypes.object,
  validationErrors: PropTypes.object,
  /* eslint-enable react/forbid-prop-types, react/require-default-props */
  dismissMessage: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onValidatedField: PropTypes.func.isRequired,
  onValidatedFields: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(ContractFormContainer);



// WEBPACK FOOTER //
// ./src/containers/ContractFormContainer.js